
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Product } from '@/components/product/ProductCard';

export function useProductOperations() {
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [productSourceToDelete, setProductSourceToDelete] = useState<'products' | 'recommendations' | null>(null);

  const duplicateProduct = async (product: Product) => {
    try {
      setIsDuplicating(true);
      setSelectedProduct(product);
      
      const { data, error } = await supabase
        .from('products')
        .insert({
          name: `${product.name} (Copy)`,
          description: product.description,
          price: product.price,
          currency: product.currency,
          category: product.category,
          image_url: product.image_url,
          affiliate_link: product.affiliate_link
        })
        .select()
        .single();
        
      if (error) throw error;
      
      toast.success('Product duplicated successfully');
      
      return true;
    } catch (error: any) {
      console.error('Error duplicating product:', error);
      toast.error(`Failed to duplicate product: ${error.message}`);
      return false;
    } finally {
      setIsDuplicating(false);
      setSelectedProduct(null);
    }
  };

  const addToProducts = async (recommendation: Product) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .insert({
          name: recommendation.name,
          description: recommendation.description,
          price: recommendation.price,
          currency: recommendation.currency,
          category: recommendation.category,
          image_url: recommendation.image_url,
          affiliate_link: recommendation.affiliate_link
        })
        .select()
        .single();
        
      if (error) throw error;
      
      toast.success('Added to products successfully');
      
      return true;
    } catch (error: any) {
      console.error('Error adding to products:', error);
      toast.error(`Failed to add to products: ${error.message}`);
      return false;
    }
  };

  const confirmDelete = (id: string, source: 'products' | 'recommendations') => {
    setProductToDelete(id);
    setProductSourceToDelete(source);
    return true; // Indicate dialog should open
  };
  
  const cancelDelete = () => {
    setProductToDelete(null);
    setProductSourceToDelete(null);
  };

  const deleteProduct = async () => {
    if (!productToDelete || !productSourceToDelete) return false;
    
    try {
      setIsDeleting(true);
      
      const { error } = await supabase
        .from(productSourceToDelete === 'products' ? 'products' : 'product_recommendations')
        .delete()
        .eq('id', productToDelete);

      if (error) {
        throw error;
      }
      
      toast.success('Product deleted successfully');
      return true;
    } catch (error: any) {
      console.error('Error deleting product:', error);
      toast.error(`Failed to delete product: ${error.message}`);
      return false;
    } finally {
      setIsDeleting(false);
      setProductToDelete(null);
      setProductSourceToDelete(null);
    }
  };

  return {
    isDuplicating,
    selectedProduct,
    isDeleting,
    productToDelete,
    productSourceToDelete,
    duplicateProduct,
    addToProducts,
    confirmDelete,
    cancelDelete,
    deleteProduct
  };
}
